import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon, SearchIcon } from 'lucide-react';
const METRO_LOGO_URL = 'https://cdn.magicpatterns.com/uploads/83gAX3TsGvnbvH5ew774Zt/Metro_Logo_HQ26.png';
/* ------------------------------------------------------------------ */
/*  Shell: enforces A3 ratio + padding + 3-row column layout           */
/* ------------------------------------------------------------------ */
function PosterFrame({
  children,
  background = '#FFFFFF',
  textColor = '#0E0E12'
}: {
  children: React.ReactNode;
  background?: string;
  textColor?: string;
}) {
  return <div className="rounded-sm overflow-hidden border border-[var(--divider)] shadow-md" style={{
    aspectRatio: '210/297',
    background,
    color: textColor
  }} data-id="element-1919">
      <div className="w-full h-full flex flex-col" style={{
      padding: '5.5%'
    }} data-id="element-1920">
        {children}
      </div>
    </div>;
}
function PosterEyebrow({
  label,
  index,
  dark = false
}: {
  label: string;
  index: string;
  dark?: boolean;
}) {
  return <div className="flex items-center justify-between font-mono" style={{
    fontSize: '6px',
    letterSpacing: '0.25em',
    color: dark ? 'rgba(255,255,255,0.55)' : '#6B6E78',
    flexShrink: 0
  }} data-id="element-1921">
      <span data-id="element-1922">METRO AGENCY · BARCELONA</span>
      <span data-id="element-1923">
        {label} · A3 · {index}
      </span>
    </div>;
}
function PosterFooter({
  category,
  dark = false,
  cta,
  ctaDark = false
}: {
  category: string;
  dark?: boolean;
  cta?: string;
  ctaDark?: boolean;
}) {
  return <div className="flex items-end justify-between" style={{
    borderTop: '1px solid ' + (dark ? 'rgba(255,255,255,0.25)' : 'rgba(14,14,18,0.15)'),
    paddingTop: '10px',
    flexShrink: 0,
    marginTop: '10px'
  }} data-id="element-1924">
      <img src={METRO_LOGO_URL} alt="me—tro" className="h-3.5 w-auto" style={{
      filter: dark ? 'brightness(0) invert(1)' : 'none',
      opacity: dark ? 0.9 : 1
    }} data-id="element-1925" />
      {cta ? <div className={'flex items-center gap-1.5 px-3 py-1 rounded-full font-space text-[9px] font-bold ' + (ctaDark ? 'bg-[var(--text-ink)] text-white' : 'bg-white text-[var(--text-ink)]')} data-id="element-1926">
          {cta}
          <ArrowRightIcon className="w-2.5 h-2.5" strokeWidth={2.5} data-id="element-1927" />
        </div> : <div className="text-right" data-id="element-1928">
          <div className="font-mono" style={{
        fontSize: '6px',
        letterSpacing: '0.25em',
        opacity: dark ? 0.6 : 0.55
      }} data-id="element-1929">
            METRO.STUDIO
          </div>
          <div className="font-mono font-bold mt-0.5" style={{
        fontSize: '6.5px',
        letterSpacing: '0.25em'
      }} data-id="element-1930">
            {category}
          </div>
        </div>}
    </div>;
}
function PriceFrom({
  price,
  suffix,
  color = '#1A2BFF',
  mutedColor = '#6B6E78',
  size = 22
}: {
  price: string;
  suffix?: string;
  color?: string;
  mutedColor?: string;
  size?: number;
}) {
  return <div className="inline-flex items-baseline gap-1.5" data-id="element-1931">
      <span className="font-mono" style={{
      fontSize: '7px',
      letterSpacing: '0.25em',
      color: mutedColor
    }} data-id="element-1932">
        FROM
      </span>
      <span className="font-space font-bold leading-none tracking-tight" style={{
      fontSize: size + 'px',
      color
    }} data-id="element-1933">
        {price}
      </span>
      {suffix && <span className="font-mono" style={{
      fontSize: '8px',
      color: mutedColor
    }} data-id="element-1934">
          {suffix}
        </span>}
    </div>;
}
function PosterCaption({
  number,
  title,
  use,
  format
}: {
  number: string;
  title: string;
  use: string;
  format: string;
}) {
  return <div className="mt-3" data-id="element-1935">
      <div className="flex items-center justify-between mb-1" data-id="element-1936">
        <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-wider" data-id="element-1937">
          {number}
        </div>
        <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-wider" data-id="element-1938">
          {format}
        </div>
      </div>
      <div className="font-space text-base font-medium leading-tight tracking-tight" data-id="element-1939">
        {title}
      </div>
      <div className="font-helvetica text-xs text-[var(--text-muted)] leading-relaxed mt-1" data-id="element-1940">
        {use}
      </div>
    </div>;
}
/* ================================================================== */
/*  11.1.1 — NETWORK MAP                                               */
/* ================================================================== */
function PosterNetworkMap() {
  const lines = [{
    color: '#1A2BFF',
    label: 'L1',
    stations: ['Brand', 'Voice', 'Strategy']
  }, {
    color: '#F26A35',
    label: 'L2',
    stations: ['Film', 'Photo', 'Sound']
  }, {
    color: '#2A8E47',
    label: 'L3',
    stations: ['SEO', 'Paid', 'CRO']
  }, {
    color: '#E8C547',
    label: 'L4',
    stations: ['Social', 'Email', 'CRM']
  }, {
    color: '#F4A523',
    label: 'L5',
    stations: ['Web', 'Events', 'Commerce']
  }];
  return <PosterFrame background="#FFFFFF" textColor="#0E0E12" data-id="element-1941">
      <PosterEyebrow label="NETWORK" index="01/11" data-id="element-1942" />

      {/* Headline */}
      <div className="mt-[7%]" style={{
      flexShrink: 0
    }} data-id="element-1943">
        <div className="font-space font-bold leading-[0.86] tracking-tight" style={{
        fontSize: '46px'
      }} data-id="element-1944">
          A network
          <br data-id="element-1945" />
          of{' '}
          <span style={{
          color: '#1A2BFF'
        }} data-id="element-1946">
            ideas.
          </span>
        </div>
      </div>

      {/* Map fills available space */}
      <div className="flex-1 min-h-0 mt-[6%] flex items-center" style={{
      marginRight: '-2%'
    }} data-id="element-1947">
        <svg viewBox="0 0 280 220" preserveAspectRatio="xMidYMid meet" className="w-full h-full" data-id="element-1948">
          {lines.map((line, lineIdx) => {
          const y = 22 + lineIdx * 42;
          const count = line.stations.length;
          const startX = 28;
          const endX = 260;
          const stepX = (endX - startX) / (count - 1);
          return <g key={lineIdx} data-id="element-1949">
                <line x1={startX} y1={y} x2={endX} y2={y} stroke={line.color} strokeWidth="4" strokeLinecap="round" data-id="element-1950" />
                <text x={0} y={y + 2} fontFamily="JetBrains Mono, monospace" fontSize="6" fontWeight="800" fill={line.color} letterSpacing="0.5" data-id="element-1951">
                  {line.label}
                </text>
                {line.stations.map((s, i) => {
              const x = startX + stepX * i;
              return <g key={i} data-id="element-1952">
                      <circle cx={x} cy={y} r="4.5" fill="#FFFFFF" stroke="#0E0E12" strokeWidth="1.5" data-id="element-1953" />
                      <text x={x} y={y - 10} fontFamily="'Space Grotesk', sans-serif" fontSize="6.5" fontWeight="700" fill="#0E0E12" textAnchor="middle" data-id="element-1954">
                        {s}
                      </text>
                    </g>;
            })}
              </g>;
        })}
        </svg>
      </div>

      {/* Subline */}
      <div className="font-helvetica leading-snug" style={{
      fontSize: '10px',
      color: '#6B6E78',
      maxWidth: '70%',
      flexShrink: 0
    }} data-id="element-1955">
        Five lines. Fifteen stations. One agency.
      </div>

      <PosterFooter category="NETWORK · BCN" data-id="element-1956" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.1.2 — SERVICE MENU                                              */
/* ================================================================== */
function PosterServiceMenu() {
  const services = [{
    n: '01',
    c: '#1A2BFF',
    name: 'Brand',
    price: '€1,200'
  }, {
    n: '02',
    c: '#E8C547',
    name: 'Website',
    price: '€390'
  }, {
    n: '03',
    c: '#F26A35',
    name: 'Content',
    price: '€350'
  }, {
    n: '04',
    c: '#2A8E47',
    name: 'Performance',
    price: '€350/mo'
  }, {
    n: '05',
    c: '#F4A523',
    name: 'Social',
    price: '€450/mo'
  }, {
    n: '06',
    c: '#0E0E12',
    name: '360°',
    price: 'Custom'
  }];
  return <PosterFrame background="#EDEEF1" textColor="#0E0E12" data-id="element-1957">
      <PosterEyebrow label="MENU" index="02/11" data-id="element-1958" />

      <div className="mt-[6%]" style={{
      flexShrink: 0
    }} data-id="element-1959">
        <div className="font-space font-bold leading-[0.86] tracking-tight" style={{
        fontSize: '34px'
      }} data-id="element-1960">
          One team.
          <br data-id="element-1961" />
          Every craft.
        </div>
      </div>

      {/* Menu list */}
      <div className="flex-1 min-h-0 flex flex-col justify-center mt-[5%]" data-id="element-1962">
        {services.map(s => <div key={s.n} className="grid items-center border-b border-[var(--text-ink)]" style={{
        gridTemplateColumns: '24px 1fr auto',
        padding: '8px 0',
        gap: '10px'
      }} data-id="element-1963">
            <div className="flex items-center gap-1.5" data-id="element-1964">
              <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
            background: s.c
          }} data-id="element-1965" />
            </div>
            <div className="min-w-0" data-id="element-1966">
              <div className="font-space font-bold tracking-tight leading-none" style={{
            fontSize: '17px'
          }} data-id="element-1967">
                {s.name}
              </div>
              <div className="font-mono mt-1" style={{
            fontSize: '6px',
            letterSpacing: '0.2em',
            color: '#6B6E78'
          }} data-id="element-1968">
                {s.n}
              </div>
            </div>
            <div className="text-right flex-shrink-0" data-id="element-1969">
              <div className="font-mono" style={{
            fontSize: '6px',
            letterSpacing: '0.25em',
            color: '#6B6E78'
          }} data-id="element-1970">
                FROM
              </div>
              <div className="font-space font-bold tracking-tight leading-none mt-0.5" style={{
            fontSize: '15px',
            color: '#1A2BFF'
          }} data-id="element-1971">
                {s.price}
              </div>
            </div>
          </div>)}
      </div>

      <PosterFooter category="MENU · BCN" cta="Start a project" ctaDark data-id="element-1972" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.1.3 — FIVE PILLARS                                              */
/* ================================================================== */
function PosterFivePillars() {
  return <PosterFrame background="#0E0E12" textColor="#FFFFFF" data-id="element-1973">
      <PosterEyebrow label="PILLARS" index="03/11" dark data-id="element-1974" />

      <div className="mt-[6%]" style={{
      flexShrink: 0
    }} data-id="element-1975">
        <div className="font-space font-bold leading-[0.86] tracking-tight" style={{
        fontSize: '36px'
      }} data-id="element-1976">
          Strategy
          <br data-id="element-1977" />
          <span style={{
          color: '#1A2BFF'
        }} data-id="element-1978">
            to screen.
          </span>
        </div>
        <div className="font-helvetica mt-3 leading-snug" style={{
        fontSize: '10px',
        color: 'rgba(255,255,255,0.7)',
        maxWidth: '85%'
      }} data-id="element-1979">
          No silos. No passing the buck.
        </div>
      </div>

      {/* Pillar grid — fills space */}
      <div className="flex-1 min-h-0 mt-[5%] grid grid-cols-2 gap-1.5" data-id="element-1980">
        {[{
        n: '01',
        t: 'Brand',
        p: '€1,200',
        bg: '#1A2BFF',
        fg: '#FFFFFF'
      }, {
        n: '02',
        t: 'Web',
        p: '€390',
        bg: '#E8C547',
        fg: '#0E0E12'
      }, {
        n: '03',
        t: 'Content',
        p: '€350',
        bg: '#F26A35',
        fg: '#FFFFFF'
      }, {
        n: '04',
        t: 'Perform',
        p: '€350/MO',
        bg: '#2A8E47',
        fg: '#FFFFFF'
      }].map(p => <div key={p.n} className="p-2.5 flex flex-col justify-between min-w-0" style={{
        background: p.bg,
        color: p.fg
      }} data-id="element-1981">
            <div className="font-mono font-bold" style={{
          fontSize: '7px',
          letterSpacing: '0.2em',
          opacity: 0.8
        }} data-id="element-1982">
              {p.n}
            </div>
            <div data-id="element-1983">
              <div className="font-space font-bold leading-none tracking-tight" style={{
            fontSize: '20px'
          }} data-id="element-1984">
                {p.t}
              </div>
              <div className="font-mono font-bold mt-2" style={{
            fontSize: '7px',
            letterSpacing: '0.18em',
            opacity: 0.9
          }} data-id="element-1985">
                FROM {p.p}
              </div>
            </div>
          </div>)}
        {/* Wide 360 block */}
        <div className="col-span-2 p-3 flex items-center justify-between min-w-0" style={{
        background: '#EDEEF1',
        color: '#0E0E12'
      }} data-id="element-1986">
          <div className="min-w-0" data-id="element-1987">
            <div className="font-mono font-bold" style={{
            fontSize: '7px',
            letterSpacing: '0.2em',
            opacity: 0.7
          }} data-id="element-1988">
              05
            </div>
            <div className="font-space font-bold leading-none tracking-tight mt-1" style={{
            fontSize: '20px'
          }} data-id="element-1989">
              360° Marketing
            </div>
          </div>
          <div className="text-right flex-shrink-0 ml-3" data-id="element-1990">
            <div className="font-mono" style={{
            fontSize: '6.5px',
            letterSpacing: '0.2em',
            color: '#6B6E78'
          }} data-id="element-1991">
              ALL OF THE ABOVE
            </div>
            <div className="font-mono font-bold mt-1" style={{
            fontSize: '8px',
            letterSpacing: '0.2em',
            color: '#1A2BFF'
          }} data-id="element-1992">
              CUSTOM
            </div>
          </div>
        </div>
      </div>

      <PosterFooter category="PILLARS · BCN" dark cta="Start a project" data-id="element-1993" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.1 — WEB DESIGN                                                */
/* ================================================================== */
function PosterWebDesign() {
  return <PosterFrame background="#1A2BFF" textColor="#FFFFFF" data-id="element-1994">
      <PosterEyebrow label="WEB" index="04/11" dark data-id="element-1995" />

      {/* Browser card */}
      <div className="mt-[6%] bg-white rounded-sm overflow-hidden text-[var(--text-ink)]" style={{
      flexShrink: 0,
      boxShadow: '0 18px 48px rgba(0,0,0,0.25)'
    }} data-id="element-1996">
        <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#F4F5F7] border-b border-[var(--divider)]" data-id="element-1997">
          <div className="w-1.5 h-1.5 rounded-full bg-[#FF5F57]" data-id="element-1998" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#FEBC2E]" data-id="element-1999" />
          <div className="w-1.5 h-1.5 rounded-full bg-[#28C840]" data-id="element-2000" />
          <div className="flex-1 ml-1.5 bg-white rounded-sm px-2 py-0.5 font-mono text-[6px] text-[var(--text-muted)]" data-id="element-2001">
            yourbrand.com
          </div>
        </div>
        <div className="p-3" data-id="element-2002">
          <div className="font-mono" style={{
          fontSize: '5px',
          letterSpacing: '0.3em',
          color: '#1A2BFF'
        }} data-id="element-2003">
            WELCOME ━━━━━
          </div>
          <div className="font-space font-bold mt-2 leading-[0.9] tracking-tight" style={{
          fontSize: '14px'
        }} data-id="element-2004">
            Hi. I'm
            <br data-id="element-2005" />
            yourbrand.
          </div>
          <div className="mt-2.5 inline-block bg-[var(--text-ink)] text-white rounded-full px-2 py-0.5 font-mono font-bold" style={{
          fontSize: '5px',
          letterSpacing: '0.15em'
        }} data-id="element-2006">
            GET IN TOUCH →
          </div>
        </div>
      </div>

      {/* Headline + price block */}
      <div className="flex-1 min-h-0 flex flex-col justify-end" data-id="element-2007">
        <div className="font-space font-bold leading-[0.88] tracking-tight" style={{
        fontSize: '34px'
      }} data-id="element-2008">
          Your website
          <br data-id="element-2009" />
          is your first
          <br data-id="element-2010" />
          hello.
        </div>
        <div className="font-helvetica mt-3 leading-snug" style={{
        fontSize: '11px',
        color: 'rgba(255,255,255,0.85)'
      }} data-id="element-2011">
          Make it count.
        </div>
        <div className="mt-3" data-id="element-2012">
          <PriceFrom price="€990" color="#FFFFFF" mutedColor="rgba(255,255,255,0.7)" size={26} data-id="element-2013" />
        </div>
      </div>

      <PosterFooter category="WEB · BCN" dark data-id="element-2014" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.2 — BRANDING                                                  */
/* ================================================================== */
function PosterBranding() {
  return <PosterFrame background="#EDEEF1" textColor="#0E0E12" data-id="element-2015">
      <PosterEyebrow label="BRAND" index="05/11" data-id="element-2016" />

      {/* Color palette strip — fixed height */}
      <div className="mt-[5%]" style={{
      flexShrink: 0
    }} data-id="element-2017">
        <div className="flex h-2.5" data-id="element-2018">
          {['#1A2BFF', '#E8C547', '#F26A35', '#2A8E47', '#F4A523', '#0E0E12'].map((c, i) => <div key={i} className="flex-1" style={{
          background: c
        }} data-id="element-2019" />)}
        </div>
        <div className="flex font-mono mt-1.5" style={{
        fontSize: '5px',
        color: '#6B6E78',
        letterSpacing: '0.18em'
      }} data-id="element-2020">
          {['P 286 C', 'P 7404', 'P 165 C', 'P 7740', 'P 137 C', 'INK'].map((c, i) => <div key={i} className="flex-1 text-center" data-id="element-2021">
                {c}
              </div>)}
        </div>
      </div>

      {/* Giant M — contained block */}
      <div className="flex-1 min-h-0 flex items-center justify-center mt-[2%]" style={{
      overflow: 'hidden'
    }} data-id="element-2022">
        <div className="font-space font-bold leading-none tracking-[-0.05em] text-center" style={{
        fontSize: '180px',
        color: '#1A2BFF'
      }} data-id="element-2023">
          M
        </div>
      </div>

      {/* Headline + price */}
      <div style={{
      flexShrink: 0
    }} data-id="element-2024">
        <div className="font-space font-bold leading-[0.9] tracking-tight" style={{
        fontSize: '22px'
      }} data-id="element-2025">
          Your brand speaks
          <br data-id="element-2026" />
          before you do.
        </div>
        <div className="mt-3 flex items-end justify-between gap-3" data-id="element-2027">
          <div className="font-helvetica leading-snug" style={{
          fontSize: '10px',
          color: '#6B6E78',
          maxWidth: '60%'
        }} data-id="element-2028">
            Logo, palette, type, guidelines.
          </div>
          <PriceFrom price="€1,200" size={22} data-id="element-2029" />
        </div>
      </div>

      <PosterFooter category="BRAND · BCN" data-id="element-2030" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.3 — VIDEO & PHOTOGRAPHY                                       */
/* ================================================================== */
function PosterVideoPhoto() {
  return <PosterFrame background="#0E0E12" textColor="#FFFFFF" data-id="element-2031">
      <PosterEyebrow label="STUDIO" index="06/11" dark data-id="element-2032" />

      {/* REC bar */}
      <div className="flex items-center gap-2 mt-2" style={{
      flexShrink: 0
    }} data-id="element-2033">
        <div className="w-1.5 h-1.5 rounded-full bg-[#F26A35]" data-id="element-2034" />
        <span className="font-mono font-bold" style={{
        fontSize: '7px',
        letterSpacing: '0.25em',
        color: '#F26A35'
      }} data-id="element-2035">
          REC
        </span>
        <span className="font-mono" style={{
        fontSize: '7px',
        letterSpacing: '0.25em',
        opacity: 0.6
      }} data-id="element-2036">
          00:00:30:00 · 4K
        </span>
      </div>

      {/* Film frame */}
      <div className="flex-1 min-h-0 relative border border-white/15 mt-[5%] flex flex-col items-center justify-center px-3 text-center" style={{
      overflow: 'hidden'
    }} data-id="element-2037">
        {/* Corner crops */}
        {[{
        top: '4px',
        left: '4px'
      }, {
        top: '4px',
        right: '4px'
      }, {
        bottom: '4px',
        left: '4px'
      }, {
        bottom: '4px',
        right: '4px'
      }].map((pos, i) => <div key={i} className="absolute" style={{
        ...pos,
        width: '10px',
        height: '10px'
      }} data-id="element-2038">
            <div className="absolute top-0 left-0 w-full h-px bg-white/40" data-id="element-2039" />
            <div className="absolute top-0 left-0 h-full w-px bg-white/40" data-id="element-2040" />
          </div>)}

        <div className="font-mono mb-3" style={{
        fontSize: '5.5px',
        letterSpacing: '0.4em',
        opacity: 0.55
      }} data-id="element-2041">
          ━━━━━ FRAME 01 ━━━━━
        </div>
        <div className="font-space font-bold leading-[0.88] tracking-tight" style={{
        fontSize: '34px'
      }} data-id="element-2042">
          Every frame
          <br data-id="element-2043" />
          earns its
          <br data-id="element-2044" />
          <span style={{
          color: '#F26A35'
        }} data-id="element-2045">
            place.
          </span>
        </div>
      </div>

      {/* Subline + price */}
      <div className="mt-[4%]" style={{
      flexShrink: 0
    }} data-id="element-2046">
        <div className="font-helvetica leading-snug" style={{
        fontSize: '11px',
        opacity: 0.85
      }} data-id="element-2047">
          Built for purpose.
        </div>
        <div className="mt-2" data-id="element-2048">
          <PriceFrom price="€350" color="#F26A35" mutedColor="rgba(255,255,255,0.6)" size={22} data-id="element-2049" />
        </div>
      </div>

      <PosterFooter category="STUDIO · BCN" dark data-id="element-2050" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.4 — SOCIAL MEDIA                                              */
/* ================================================================== */
function PosterSocialMedia() {
  return <PosterFrame background="#FFFFFF" textColor="#0E0E12" data-id="element-2051">
      <PosterEyebrow label="SOCIAL" index="07/11" data-id="element-2052" />

      {/* Story progress bars */}
      <div className="flex gap-1 mt-2" style={{
      flexShrink: 0
    }} data-id="element-2053">
        {[100, 100, 60, 0, 0].map((p, i) => <div key={i} className="flex-1 h-0.5 bg-[var(--divider)] rounded-full overflow-hidden" data-id="element-2054">
            <div className="h-full" style={{
          width: p + '%',
          background: '#0E0E12'
        }} data-id="element-2055" />
          </div>)}
      </div>

      {/* Decorative post grid — fixed height ratio */}
      <div className="mt-2 grid grid-cols-3 gap-1" style={{
      flexShrink: 0,
      aspectRatio: '3/2'
    }} data-id="element-2056">
        {['#1A2BFF', '#EDEEF1', '#F26A35', '#E8C547', '#0E0E12', '#2A8E47'].map((b, i) => <div key={i} style={{
        background: b
      }} data-id="element-2057" />)}
      </div>

      {/* Q&A in flex region */}
      <div className="flex-1 min-h-0 flex flex-col justify-center mt-[4%]" data-id="element-2058">
        <div className="flex items-start gap-2.5" data-id="element-2059">
          <span className="font-mono font-bold flex-shrink-0" style={{
          fontSize: '16px',
          color: '#1A2BFF'
        }} data-id="element-2060">
            Q.
          </span>
          <span className="font-space font-bold leading-[0.92] tracking-tight" style={{
          fontSize: '22px'
        }} data-id="element-2061">
            Who posts
            <br data-id="element-2062" />
            for you?
          </span>
        </div>
        <div className="flex items-start gap-2.5 mt-3 pl-5" data-id="element-2063">
          <span className="font-mono font-bold flex-shrink-0" style={{
          fontSize: '16px',
          color: '#1A2BFF'
        }} data-id="element-2064">
            A.
          </span>
          <span className="font-space font-bold leading-[0.92] tracking-tight" style={{
          fontSize: '32px',
          color: '#1A2BFF'
        }} data-id="element-2065">
            We do.
          </span>
        </div>
      </div>

      {/* Price block */}
      <div className="mt-3" style={{
      flexShrink: 0
    }} data-id="element-2066">
        <PriceFrom price="€450" suffix="/ month" size={22} data-id="element-2067" />
      </div>

      <PosterFooter category="SOCIAL · BCN" data-id="element-2068" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.5 — MARKETING 360°                                            */
/* ================================================================== */
function PosterMarketing360() {
  return <PosterFrame background="#EDEEF1" textColor="#0E0E12" data-id="element-2069">
      <PosterEyebrow label="360°" index="08/11" data-id="element-2070" />

      {/* Map area — flex 1 */}
      <div className="flex-1 min-h-0 mt-[5%] relative" data-id="element-2071">
        <svg viewBox="0 0 280 360" preserveAspectRatio="xMidYMid meet" className="absolute inset-0 w-full h-full" data-id="element-2072">
          <path d="M -10 60 L 80 60 L 140 120 L 290 120" stroke="#1A2BFF" strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-2073" />
          <path d="M -10 130 L 60 130 L 100 90 L 200 90 L 240 130 L 290 130" stroke="#E8C547" strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-2074" />
          <path d="M -10 200 L 100 200 L 160 260 L 290 260" stroke="#F26A35" strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-2075" />
          <path d="M -10 290 L 80 290 L 130 340 L 290 340" stroke="#2A8E47" strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-2076" />
          <path d="M 60 -10 L 60 290 L 130 340" stroke="#F4A523" strokeWidth="5" fill="none" strokeLinejoin="round" strokeLinecap="round" data-id="element-2077" />
          {[[80, 60], [140, 120], [60, 130], [100, 90], [200, 90], [240, 130], [100, 200], [160, 260], [80, 290], [130, 340], [60, 60]].map(([x, y], i) => <circle key={i} cx={x} cy={y} r="5" fill="#FFFFFF" stroke="#0E0E12" strokeWidth="1.8" data-id="element-2078" />)}
        </svg>

        {/* Black overlay band centered */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2" data-id="element-2079">
          <div className="bg-[var(--text-ink)] text-white mx-2 px-3 py-3" data-id="element-2080">
            <div className="font-mono mb-1.5" style={{
            fontSize: '6px',
            letterSpacing: '0.3em',
            opacity: 0.7
          }} data-id="element-2081">
              MARKETING 360°
            </div>
            <div className="font-space font-bold leading-[0.88] tracking-tight" style={{
            fontSize: '30px'
          }} data-id="element-2082">
              Not a menu.
              <br data-id="element-2083" />A{' '}
              <span style={{
              color: '#1A2BFF'
            }} data-id="element-2084">
                system.
              </span>
            </div>
            <div className="font-helvetica leading-snug mt-2" style={{
            fontSize: '9px',
            opacity: 0.85
          }} data-id="element-2085">
              Strategy. Creative. Production. One team.
            </div>
          </div>
        </div>
      </div>

      <PosterFooter category="360° · BCN" data-id="element-2086" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.6 — MICRO / LOCAL                                             */
/* ================================================================== */
function PosterMicroPack() {
  return <PosterFrame background="#FFFFFF" textColor="#0E0E12" data-id="element-2087">
      <PosterEyebrow label="LOCAL" index="09/11" data-id="element-2088" />

      {/* Search bar */}
      <div className="mt-[5%]" style={{
      flexShrink: 0
    }} data-id="element-2089">
        <div className="flex items-center gap-2 border border-[var(--divider)] rounded-full px-3 py-1.5 bg-white" data-id="element-2090">
          <SearchIcon className="w-2.5 h-2.5 text-[var(--text-muted)]" data-id="element-2091" />
          <span className="font-helvetica flex-1 truncate" style={{
          fontSize: '8px',
          color: '#6B6E78'
        }} data-id="element-2092">
            your business · barcelona
          </span>
          <span className="font-mono" style={{
          fontSize: '6px',
          letterSpacing: '0.25em',
          color: '#6B6E78'
        }} data-id="element-2093">
            G
          </span>
        </div>
        <div className="mt-2 font-mono" style={{
        fontSize: '6px',
        letterSpacing: '0.25em',
        color: '#6B6E78'
      }} data-id="element-2094">
          ABOUT 0 RESULTS · (0.00 SECONDS)
        </div>
        <div className="mt-1.5 h-px bg-[var(--divider)]" data-id="element-2095" />
        <div className="mt-1.5 space-y-1" data-id="element-2096">
          {[1, 2].map(i => <div key={i} className="space-y-0.5 opacity-25" data-id="element-2097">
              <div className="h-0.5 bg-[var(--divider)] rounded-full w-1/2" data-id="element-2098" />
              <div className="h-0.5 bg-[var(--divider)] rounded-full w-2/3" data-id="element-2099" />
            </div>)}
        </div>
      </div>

      {/* Headline */}
      <div className="flex-1 min-h-0 flex flex-col justify-center mt-[4%]" data-id="element-2100">
        <div className="font-space font-bold leading-[0.88] tracking-tight" style={{
        fontSize: '30px'
      }} data-id="element-2101">
          Your business
          <br data-id="element-2102" />
          is not on
          <br data-id="element-2103" />
          <span style={{
          color: '#1A2BFF',
          textDecoration: 'line-through',
          textDecorationColor: '#0E0E12',
          textDecorationThickness: '2px'
        }} data-id="element-2104">
            Google.
          </span>
        </div>
        <div className="font-space font-bold mt-3" style={{
        fontSize: '18px'
      }} data-id="element-2105">
          We fix that.
        </div>
        <div className="font-helvetica mt-2 leading-snug" style={{
        fontSize: '10px',
        color: '#6B6E78'
      }} data-id="element-2106">
          One page. 72 hours. Done.
        </div>
      </div>

      {/* Price + locations */}
      <div className="mt-[3%]" style={{
      flexShrink: 0
    }} data-id="element-2107">
        <div className="flex items-baseline justify-between gap-2 border-t border-[var(--text-ink)] pt-2" data-id="element-2108">
          <PriceFrom price="€390" size={36} data-id="element-2109" />
          <span className="font-mono" style={{
          fontSize: '6px',
          letterSpacing: '0.22em',
          color: '#6B6E78'
        }} data-id="element-2110">
            BCN · SBD · TRS
          </span>
        </div>
      </div>

      <PosterFooter category="LOCAL · BCN" cta="Start a project" ctaDark data-id="element-2111" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.7 — SEO & PERFORMANCE                                         */
/* ================================================================== */
function PosterSEO() {
  return <PosterFrame background="#FFFFFF" textColor="#0E0E12" data-id="element-2112">
      <PosterEyebrow label="SEO" index="10/11" data-id="element-2113" />

      {/* Headline */}
      <div className="mt-[5%]" style={{
      flexShrink: 0
    }} data-id="element-2114">
        <div className="font-space font-bold leading-[0.88] tracking-tight" style={{
        fontSize: '24px'
      }} data-id="element-2115">
          Page 2 of
          <br data-id="element-2116" />
          Google is
          <br data-id="element-2117" />
          where brands
          <br data-id="element-2118" />
          <span style={{
          color: '#1A2BFF'
        }} data-id="element-2119">
            go to hide.
          </span>
        </div>
      </div>

      {/* SERP stacks */}
      <div className="flex-1 min-h-0 mt-[5%] flex flex-col justify-center gap-3" data-id="element-2120">
        {/* Page 1 */}
        <div data-id="element-2121">
          <div className="flex items-center gap-2 mb-1.5" data-id="element-2122">
            <div className="font-mono font-bold px-2 py-0.5 rounded-full" style={{
            background: '#1A2BFF',
            color: '#FFFFFF',
            fontSize: '6px',
            letterSpacing: '0.25em'
          }} data-id="element-2123">
              PAGE 1
            </div>
            <div className="font-mono" style={{
            fontSize: '6px',
            letterSpacing: '0.2em',
            color: '#6B6E78'
          }} data-id="element-2124">
              ← visible
            </div>
          </div>
          {['yourbrand.com', 'yourbrand.com/services'].map(u => <div key={u} className="mb-1 border-l-2 pl-2 py-0.5" style={{
          borderColor: '#1A2BFF'
        }} data-id="element-2125">
              <div className="font-mono" style={{
            fontSize: '6px',
            color: '#6B6E78'
          }} data-id="element-2126">
                {u}
              </div>
              <div className="font-space font-bold leading-tight" style={{
            fontSize: '10px',
            color: '#1A2BFF'
          }} data-id="element-2127">
                Yourbrand — Welcome.
              </div>
            </div>)}
        </div>

        {/* Page 2 */}
        <div className="opacity-30" data-id="element-2128">
          <div className="flex items-center gap-2 mb-1.5" data-id="element-2129">
            <div className="font-mono font-bold px-2 py-0.5 rounded-full border border-[var(--text-ink)]" style={{
            fontSize: '6px',
            letterSpacing: '0.25em'
          }} data-id="element-2130">
              PAGE 2
            </div>
            <div className="font-mono" style={{
            fontSize: '6px',
            letterSpacing: '0.2em',
            color: '#6B6E78'
          }} data-id="element-2131">
              ← invisible
            </div>
          </div>
          {['competitor.com'].map(u => <div key={u} className="mb-1 pl-2.5 py-0.5" data-id="element-2132">
              <div className="font-mono" style={{
            fontSize: '6px',
            color: '#6B6E78'
          }} data-id="element-2133">
                {u}
              </div>
              <div className="font-space font-bold leading-tight" style={{
            fontSize: '10px'
          }} data-id="element-2134">
                Competitor — home
              </div>
            </div>)}
        </div>
      </div>

      {/* Subline + price */}
      <div className="border-t border-[var(--text-ink)] pt-2 mt-[3%] flex items-baseline justify-between gap-3" style={{
      flexShrink: 0
    }} data-id="element-2135">
        <div className="font-space font-bold leading-tight tracking-tight" style={{
        fontSize: '13px',
        maxWidth: '55%'
      }} data-id="element-2136">
          We get you found.
        </div>
        <PriceFrom price="€350" suffix="/ month" size={20} data-id="element-2137" />
      </div>

      <PosterFooter category="SEO · BCN" data-id="element-2138" />
    </PosterFrame>;
}
/* ================================================================== */
/*  11.2.8 — EVENTS & STREAMING                                        */
/* ================================================================== */
function PosterEvents() {
  return <PosterFrame background="#0E0E12" textColor="#FFFFFF" data-id="element-2139">
      <PosterEyebrow label="EVENTS" index="11/11" dark data-id="element-2140" />

      {/* LIVE bar */}
      <div className="flex items-center justify-between mt-2" style={{
      flexShrink: 0
    }} data-id="element-2141">
        <div className="flex items-center gap-2" data-id="element-2142">
          <div className="px-1.5 py-0.5 rounded-sm font-mono font-bold flex items-center gap-1" style={{
          background: '#F26A35',
          color: '#FFFFFF',
          fontSize: '7px',
          letterSpacing: '0.25em'
        }} data-id="element-2143">
            <div className="w-1 h-1 bg-white rounded-full animate-pulse" data-id="element-2144" />
            LIVE
          </div>
          <span className="font-mono" style={{
          fontSize: '6.5px',
          letterSpacing: '0.2em',
          opacity: 0.7
        }} data-id="element-2145">
            1,247 WATCHING
          </span>
        </div>
        <span className="font-mono" style={{
        fontSize: '6.5px',
        letterSpacing: '0.2em',
        opacity: 0.7
      }} data-id="element-2146">
          1080P · 60FPS
        </span>
      </div>

      {/* Stream window */}
      <div className="mt-[5%] border border-white/20 relative" style={{
      flexShrink: 0,
      aspectRatio: '16/9'
    }} data-id="element-2147">
        <div className="absolute inset-0 flex items-center justify-center" data-id="element-2148">
          <div className="font-space font-bold tracking-tight text-center" style={{
          fontSize: '40px',
          lineHeight: 0.85,
          background: 'linear-gradient(180deg, #FFFFFF 0%, rgba(255,255,255,0.35) 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }} data-id="element-2149">
            ON
            <br data-id="element-2150" />
            AIR
          </div>
        </div>
        <div className="absolute left-2 right-2" style={{
        bottom: '5px'
      }} data-id="element-2151">
          <div className="h-0.5 bg-white/15 rounded-full overflow-hidden" data-id="element-2152">
            <div className="h-full" style={{
            width: '38%',
            background: '#F26A35'
          }} data-id="element-2153" />
          </div>
          <div className="flex justify-between font-mono mt-0.5" style={{
          fontSize: '5px',
          opacity: 0.55
        }} data-id="element-2154">
            <span data-id="element-2155">00:14:22</span>
            <span data-id="element-2156">00:38:00</span>
          </div>
        </div>
      </div>

      {/* Headline + subline */}
      <div className="flex-1 min-h-0 flex flex-col justify-end" data-id="element-2157">
        <div className="font-space font-bold leading-[0.9] tracking-tight" style={{
        fontSize: '24px'
      }} data-id="element-2158">
          Your event
          <br data-id="element-2159" />
          deserves
          <br data-id="element-2160" />
          more than a
          <br data-id="element-2161" />
          phone on a
          <br data-id="element-2162" />
          <span style={{
          color: '#F26A35'
        }} data-id="element-2163">
            tripod.
          </span>
        </div>
        <div className="font-helvetica mt-3 leading-snug" style={{
        fontSize: '10px',
        opacity: 0.85
      }} data-id="element-2164">
          Production. Streaming. One team.
        </div>
      </div>

      <PosterFooter category="EVENTS · BCN" dark data-id="element-2165" />
    </PosterFrame>;
}
/* ================================================================== */
/*  Section                                                            */
/* ================================================================== */
export function PostersSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section id="posters" ref={ref} className="min-h-screen px-8 py-24" data-id="element-2166">
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={isInView ? {
      opacity: 1,
      y: 0
    } : {}} transition={{
      duration: 0.5,
      ease: 'easeOut'
    }} className="max-w-7xl mx-auto" data-id="element-2167">
        <div className="mb-16 max-w-3xl" data-id="element-2168">
          <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-4" data-id="element-2169">
            11 / POSTERS
          </div>
          <h2 className="font-space text-5xl md:text-6xl font-medium tracking-tight" data-id="element-2170">
            Made to be seen.
          </h2>
          <p className="text-lg text-[var(--text-muted)] mt-4 font-helvetica leading-relaxed" data-id="element-2171">
            Eleven posters at A3 portrait. Each one runs on the same skeleton —
            eyebrow, hero, subline, price, footer — so they read as one family.
            Each has a single graphic idea earning the wall: a five-line
            network, a giant blue M, a film slate, a stream UI, a search with
            zero results.
          </p>
        </div>

        {/* General */}
        <div className="mb-20" data-id="element-2172">
          <div className="flex items-baseline justify-between mb-8" data-id="element-2173">
            <div data-id="element-2174">
              <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-2" data-id="element-2175">
                11.1 / GENERAL
              </div>
              <h3 className="font-space text-3xl font-bold tracking-tight" data-id="element-2176">
                Three for any wall.
              </h3>
            </div>
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-wider" data-id="element-2177">
              A3 PORTRAIT · 297 × 420 MM
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2178">
            <div data-id="element-2179">
              <PosterNetworkMap data-id="element-2180" />
              <PosterCaption number="11.1.1" title="Network map" use="Five lines, fifteen stations. The agency as one graphic." format="A3 · CMYK" data-id="element-2181" />
            </div>
            <div data-id="element-2182">
              <PosterServiceMenu data-id="element-2183" />
              <PosterCaption number="11.1.2" title="Service menu" use="Prices visible at a glance. For shop windows and receptions." format="A3 · CMYK" data-id="element-2184" />
            </div>
            <div data-id="element-2185">
              <PosterFivePillars data-id="element-2186" />
              <PosterCaption number="11.1.3" title="Five pillars" use="Full-bleed black with color blocks. High-impact lobby piece." format="A3 · CMYK" data-id="element-2187" />
            </div>
          </div>
        </div>

        {/* Service-specific */}
        <div className="mb-20" data-id="element-2188">
          <div className="flex items-baseline justify-between mb-8" data-id="element-2189">
            <div data-id="element-2190">
              <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-2" data-id="element-2191">
                11.2 / SERVICE-SPECIFIC
              </div>
              <h3 className="font-space text-3xl font-bold tracking-tight" data-id="element-2192">
                Eight, one per service.
              </h3>
            </div>
            <div className="font-mono text-[10px] text-[var(--text-muted)] tracking-wider" data-id="element-2193">
              A3 PORTRAIT · 297 × 420 MM
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-id="element-2194">
            <div data-id="element-2195">
              <PosterWebDesign data-id="element-2196" />
              <PosterCaption number="11.2.1" title="Web Design" use="The poster IS the website. A browser card sits at the top, headline as the page." format="A3" data-id="element-2197" />
            </div>
            <div data-id="element-2198">
              <PosterBranding data-id="element-2199" />
              <PosterCaption number="11.2.2" title="Branding" use="A giant blue M owning the canvas. Pantones at the top, palette at a glance." format="A3" data-id="element-2200" />
            </div>
            <div data-id="element-2201">
              <PosterVideoPhoto data-id="element-2202" />
              <PosterCaption number="11.2.3" title="Video & Photography" use="Film slate aesthetic. Title card in the safe area. Orange REC accent." format="A3" data-id="element-2203" />
            </div>
            <div data-id="element-2204">
              <PosterSocialMedia data-id="element-2205" />
              <PosterCaption number="11.2.4" title="Social Media" use="Instagram story bars + post grid + Q/A. Native to where the work lives." format="A3" data-id="element-2206" />
            </div>
            <div data-id="element-2207">
              <PosterMarketing360 data-id="element-2208" />
              <PosterCaption number="11.2.5" title="Marketing 360°" use="Full transit map. Black band carries the headline mid-poster." format="A3" data-id="element-2209" />
            </div>
            <div data-id="element-2210">
              <PosterMicroPack data-id="element-2211" />
              <PosterCaption number="11.2.6" title="Local · Micro" use="Fake Google returning zero results. Strike-through. €390." format="A3" data-id="element-2212" />
            </div>
            <div data-id="element-2213">
              <PosterSEO data-id="element-2214" />
              <PosterCaption number="11.2.7" title="SEO & Performance" use="Two SERP stacks. Page 1 bold, Page 2 faded. The argument is the design." format="A3" data-id="element-2215" />
            </div>
            <div data-id="element-2216">
              <PosterEvents data-id="element-2217" />
              <PosterCaption number="11.2.8" title="Events & Streaming" use="Live stream UI on ink black. ON AIR card with scrubber + viewer count." format="A3" data-id="element-2218" />
            </div>
          </div>
        </div>

        {/* Copy rules */}
        <div data-id="element-2219">
          <div className="mb-8" data-id="element-2220">
            <div className="font-mono text-xs text-[var(--text-muted)] tracking-wider mb-2" data-id="element-2221">
              11.3 / COPY RULES
            </div>
            <h3 className="font-space text-3xl font-bold tracking-tight" data-id="element-2222">
              For the posters that come next.
            </h3>
            <p className="text-sm text-[var(--text-muted)] mt-3 max-w-2xl font-helvetica leading-relaxed" data-id="element-2223">
              The system for writing any future Metro poster. Pasted into the
              brief, this gets you a poster that reads from far, lands up close,
              and matches the rest of the wall.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-2224">
            {[{
            tag: 'HEADLINE',
            title: '3–8 words',
            body: 'Reads at 5 metres. Space Grotesk Bold. The single thing the viewer should take away if they only see one thing.',
            sample: 'Your website is your first hello.'
          }, {
            tag: 'SUBLINE',
            title: '6–12 words',
            body: 'Reads at 2 metres. Helvetica Neue Regular. Adds the second beat — the offer, the proof, the next step.',
            sample: 'Make it count.'
          }, {
            tag: 'PRICE',
            title: 'Always "From €X"',
            body: 'Honesty. No starred footnotes, no "starting at". If recurring, "from €X / month". Mono keeps it legible at distance.',
            sample: 'from €450 / month'
          }, {
            tag: 'CTA',
            title: 'One line max',
            body: 'Web OR phone. Never both. If a poster needs two CTAs, it needs two posters. Always ending with a period.',
            sample: 'Start a project →'
          }, {
            tag: 'WHITE SPACE',
            title: '70% air',
            body: 'If it looks empty, it is right. Posters compete with everything else on the wall — only the work that needs to land, lands.',
            sample: '— empty space is the design —'
          }, {
            tag: 'PUNCTUATION',
            title: 'Periods. Never exclamations.',
            body: 'Confidence is quiet. The work argues for itself. One exclamation across eleven posters: that is the system.',
            sample: 'We do.  ✗ We do!'
          }].map(r => <div key={r.tag} className="border border-[var(--divider)] rounded-sm p-5 bg-[var(--bg-surface)]" data-id="element-2225">
                <div className="flex items-baseline justify-between mb-3" data-id="element-2226">
                  <span className="font-mono text-[10px] text-[var(--accent-blue)] font-bold tracking-widest" data-id="element-2227">
                    {r.tag}
                  </span>
                  <span className="font-mono text-[9px] text-[var(--text-muted)] tracking-wider" data-id="element-2228">
                    RULE
                  </span>
                </div>
                <div className="font-space font-bold text-lg tracking-tight leading-tight mb-2" data-id="element-2229">
                  {r.title}
                </div>
                <p className="font-helvetica text-xs text-[var(--text-muted)] leading-relaxed mb-3" data-id="element-2230">
                  {r.body}
                </p>
                <div className="border-l-2 border-[var(--accent-blue)] pl-3 py-1" data-id="element-2231">
                  <div className="font-mono text-[9px] text-[var(--text-muted)] tracking-widest mb-1" data-id="element-2232">
                    SAMPLE
                  </div>
                  <div className="font-space text-sm font-medium italic" data-id="element-2233">
                    {r.sample}
                  </div>
                </div>
              </div>)}
          </div>

          {/* Print spec */}
          <div className="mt-8 bg-[var(--text-ink)] text-white rounded-sm p-6 flex items-start gap-6" data-id="element-2234">
            <div className="font-mono text-[9px] tracking-widest text-[var(--accent-blue)] font-bold flex-shrink-0" data-id="element-2235">
              PRINT SPEC
            </div>
            <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm" data-id="element-2236">
              <div data-id="element-2237">
                <div className="font-mono text-[9px] opacity-60 tracking-widest mb-1" data-id="element-2238">
                  SIZE
                </div>
                <div className="font-space font-bold text-base" data-id="element-2239">
                  A3 · 297 × 420 mm
                </div>
                <div className="text-[10px] opacity-70 mt-1" data-id="element-2240">
                  Portrait. Bleed 3 mm.
                </div>
              </div>
              <div data-id="element-2241">
                <div className="font-mono text-[9px] opacity-60 tracking-widest mb-1" data-id="element-2242">
                  PAPER
                </div>
                <div className="font-space font-bold text-base" data-id="element-2243">
                  170 g uncoated
                </div>
                <div className="text-[10px] opacity-70 mt-1" data-id="element-2244">
                  Matte. No varnish.
                </div>
              </div>
              <div data-id="element-2245">
                <div className="font-mono text-[9px] opacity-60 tracking-widest mb-1" data-id="element-2246">
                  COLOUR
                </div>
                <div className="font-space font-bold text-base" data-id="element-2247">
                  CMYK + Pantone
                </div>
                <div className="text-[10px] opacity-70 mt-1" data-id="element-2248">
                  Metro Blue: Pantone 286 C.
                </div>
              </div>
              <div data-id="element-2249">
                <div className="font-mono text-[9px] opacity-60 tracking-widest mb-1" data-id="element-2250">
                  FILES
                </div>
                <div className="font-space font-bold text-base" data-id="element-2251">
                  PDF/X-1a + AI
                </div>
                <div className="text-[10px] opacity-70 mt-1" data-id="element-2252">
                  Fonts outlined for print.
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>;
}